import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Workflow, WorkflowService } from "../../../services/WorkflowService";
import { ApiKeyOrGetter } from "../../../services/GeneralService";
import WorkflowPayloadFields from "../../Studio/WorkflowPayloadFields";
import {
  parseWorkflowRequestBody,
  DEFAULT_WORKFLOW_INPUT_JSON,
  DEFAULT_WORKFLOW_MESSAGES_JSON,
  type WorkflowPayloadMode,
} from "../../Studio/workflowRequestUtils";
import { Button } from "../../ui/button";
import { Checkbox } from "../../ui/checkbox";
import { Input } from "../../ui/input";
import { Label } from "../../ui/label";
import { Textarea } from "../../ui/textarea";
import {
  ChevronDownIcon,
  ChevronRightIcon,
  CopyIcon,
  Loader2Icon,
  PlayIcon,
} from "lucide-react";
import { toast } from "sonner";

function formatJson(obj: unknown): string {
  try {
    const s = JSON.stringify(obj, null, 2);
    return s === undefined ? String(obj) : s;
  } catch {
    return String(obj);
  }
}

function formatMs(ms: number): string {
  return ms >= 1000 ? `${(ms / 1000).toFixed(2)}s` : `${ms}ms`;
}

function workflowAssistantContent(data: unknown): string | null {
  if (data === null || typeof data !== "object") return null;
  const d = data as Record<string, unknown>;
  const choices = d.choices;
  if (!Array.isArray(choices) || choices.length === 0) return null;
  const first = choices[0];
  if (first === null || typeof first !== "object") return null;
  const msg = (first as Record<string, unknown>).message;
  if (msg === null || typeof msg !== "object") return null;
  const raw = (msg as Record<string, unknown>).content;
  if (typeof raw === "string") return raw;
  if (raw !== undefined) {
    try {
      return JSON.stringify(raw, null, 2);
    } catch {
      return String(raw);
    }
  }
  return null;
}

function errorMessage(err: unknown): string {
  if (typeof err === "string") return err;
  if (err && typeof err === "object") {
    const o = err as Record<string, unknown>;
    if (typeof o.message === "string") return o.message;
    const nested = o.error;
    if (nested && typeof nested === "object" && nested !== null) {
      const m = (nested as Record<string, unknown>).message;
      if (typeof m === "string") return m;
    }
  }
  return "Request failed";
}

interface TimedChunk {
  type: string;
  content: unknown;
  metadata?: Record<string, unknown>;
  receivedMs: number;
}

interface SectionProps {
  title: string;
  children: React.ReactNode;
  defaultOpen?: boolean;
}

const Section: React.FC<SectionProps> = ({ title, children, defaultOpen = true }) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="border border-border rounded-lg">
      <button
        type="button"
        className="flex items-center gap-2 w-full px-4 py-2.5 text-sm font-medium text-left hover:bg-muted/50 transition-colors rounded-t-lg"
        onClick={() => setOpen(!open)}
      >
        {open ? (
          <ChevronDownIcon className="h-4 w-4 text-muted-foreground" />
        ) : (
          <ChevronRightIcon className="h-4 w-4 text-muted-foreground" />
        )}
        {title}
      </button>
      {open && <div className="px-4 pb-4 space-y-4">{children}</div>}
    </div>
  );
};

interface WorkflowTestRequestTabProps {
  workflow: Workflow;
  apiKey: ApiKeyOrGetter;
}

const WorkflowTestRequestTab: React.FC<WorkflowTestRequestTabProps> = ({
  workflow,
  apiKey,
}) => {
  const [payloadMode, setPayloadMode] = useState<WorkflowPayloadMode>("input");
  const [inputJson, setInputJson] = useState(DEFAULT_WORKFLOW_INPUT_JSON);
  const [messagesJson, setMessagesJson] = useState(DEFAULT_WORKFLOW_MESSAGES_JSON);
  const [forgeReuseSession, setForgeReuseSession] = useState(false);
  const [secretsJson, setSecretsJson] = useState("");
  const [metadataJson, setMetadataJson] = useState("");
  const [sessionIdOverride, setSessionIdOverride] = useState("");
  const [includeProfile, setIncludeProfile] = useState(false);
  const [running, setRunning] = useState(false);
  const [streamChunks, setStreamChunks] = useState<TimedChunk[]>([]);
  const [completion, setCompletion] = useState<unknown>(null);
  const [completionMs, setCompletionMs] = useState<number | null>(null);
  const [errorText, setErrorText] = useState<string | null>(null);
  const [lastSessionId, setLastSessionId] = useState<string | null>(null);
  const runAbortRef = useRef<AbortController | null>(null);
  const runStartRef = useRef<number>(0);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const modelRef = useMemo(
    () => `workflow:${workflow.workflow_ref_name}`,
    [workflow.workflow_ref_name]
  );

  const assistantPreview = useMemo(() => {
    if (completion === null) return null;
    const assistant = workflowAssistantContent(completion);
    if (assistant === null || assistant === "") return null;
    return assistant;
  }, [completion]);

  const hasResponse = streamChunks.length > 0 || completion !== null || errorText !== null;

  const handleRun = useCallback(() => {
    const parsed = parseWorkflowRequestBody(payloadMode, inputJson, messagesJson);
    if (parsed.ok === false) {
      toast.error(parsed.error);
      return;
    }

    let secrets: Record<string, unknown> | undefined;
    if (secretsJson.trim()) {
      try {
        const s = JSON.parse(secretsJson) as unknown;
        if (s === null || typeof s !== "object" || Array.isArray(s)) {
          toast.error("Secrets must be a JSON object.");
          return;
        }
        secrets = s as Record<string, unknown>;
      } catch {
        toast.error("Invalid JSON in secrets.");
        return;
      }
    }

    let metadata: Record<string, unknown> | undefined;
    if (metadataJson.trim()) {
      try {
        const m = JSON.parse(metadataJson) as unknown;
        if (m === null || typeof m !== "object" || Array.isArray(m)) {
          toast.error("Metadata must be a JSON object.");
          return;
        }
        metadata = m as Record<string, unknown>;
      } catch {
        toast.error("Invalid JSON in metadata.");
        return;
      }
    }

    const override = sessionIdOverride.trim();
    let sid: string;
    if (override) {
      sid = override;
    } else if (forgeReuseSession && lastSessionId) {
      sid = lastSessionId;
    } else {
      sid = crypto.randomUUID();
    }

    const payload: Record<string, unknown> = {
      model: modelRef,
      session_id: sid,
    };
    if (parsed.input !== undefined) payload.input = parsed.input;
    if (parsed.messages !== undefined) payload.messages = parsed.messages;
    if (secrets) payload.secrets = secrets;
    if (metadata) payload.metadata = metadata;

    runAbortRef.current?.abort();
    const ac = new AbortController();
    runAbortRef.current = ac;

    setRunning(true);
    setStreamChunks([]);
    setCompletion(null);
    setCompletionMs(null);
    setErrorText(null);
    setLastSessionId(sid);
    runStartRef.current = performance.now();

    void WorkflowService.runWorkflowCompletionStreaming(
      payload,
      apiKey,
      includeProfile,
      ac.signal,
      (event) => {
        if (ac.signal.aborted || !mountedRef.current) return;
        const elapsed = Math.round(performance.now() - runStartRef.current);

        if (event.type === "chunk") {
          const d = event.data as Record<string, unknown>;
          setStreamChunks((prev) => [
            ...prev,
            {
              type: (d.type as string) || "chunk",
              content: d.content,
              metadata: d.metadata as Record<string, unknown> | undefined,
              receivedMs: elapsed,
            },
          ]);
        } else if (event.type === "completion") {
          setCompletion(event.data);
          const serverMs = (event as Record<string, unknown>).elapsed_ms;
          setCompletionMs(typeof serverMs === "number" ? serverMs : elapsed);
        } else if (event.type === "error") {
          setErrorText(errorMessage(event.data));
          setCompletionMs(elapsed);
        }
      }
    )
      .then(() => {
        if (ac.signal.aborted || !mountedRef.current) return;
        if (!errorText) toast.success("Workflow completed");
      })
      .catch((err: unknown) => {
        if (ac.signal.aborted || !mountedRef.current) return;
        if (err instanceof DOMException && err.name === "AbortError") return;
        const elapsed = Math.round(performance.now() - runStartRef.current);
        setCompletionMs(elapsed);
        setErrorText(errorMessage(err));
        toast.error(errorMessage(err));
      })
      .finally(() => {
        if (!ac.signal.aborted && mountedRef.current) setRunning(false);
      });
  }, [
    payloadMode, inputJson, messagesJson, secretsJson, metadataJson,
    sessionIdOverride, forgeReuseSession, lastSessionId, modelRef,
    apiKey, includeProfile, errorText,
  ]);

  const copySession = () => {
    if (lastSessionId) {
      void navigator.clipboard.writeText(lastSessionId);
      toast.success("Session ID copied");
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">
          Model:{" "}
          <code className="bg-muted px-1.5 py-0.5 rounded font-mono text-xs">{modelRef}</code>
        </p>
        <Button type="button" onClick={handleRun} disabled={running} className="gap-2">
          {running ? (
            <Loader2Icon className="h-4 w-4 animate-spin" />
          ) : (
            <PlayIcon className="h-4 w-4" />
          )}
          Run
        </Button>
      </div>

      {/* ── Request ── */}
      <Section title="Request">
        <WorkflowPayloadFields
          variant="forge"
          mode={payloadMode}
          onModeChange={setPayloadMode}
          inputJson={inputJson}
          onInputJsonChange={setInputJson}
          messagesJson={messagesJson}
          onMessagesJsonChange={setMessagesJson}
          reuseSession={forgeReuseSession}
          onReuseSessionChange={setForgeReuseSession}
          showReuseSession
          showDescription={false}
          disabled={running}
        />
      </Section>

      {/* ── Options ── */}
      <Section title="Options" defaultOpen={false}>
        <div className="space-y-2">
          <Label htmlFor="wf-test-secrets">Secrets (JSON object)</Label>
          <Textarea
            id="wf-test-secrets"
            className="font-mono text-sm min-h-[72px]"
            value={secretsJson}
            onChange={(e) => setSecretsJson(e.target.value)}
            placeholder="{}"
            spellCheck={false}
            disabled={running}
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="wf-test-meta">Metadata (JSON object)</Label>
          <Textarea
            id="wf-test-meta"
            className="font-mono text-sm min-h-[72px]"
            value={metadataJson}
            onChange={(e) => setMetadataJson(e.target.value)}
            placeholder="{}"
            spellCheck={false}
            disabled={running}
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="wf-test-sid">Session ID override</Label>
          <Input
            id="wf-test-sid"
            className="font-mono text-sm"
            value={sessionIdOverride}
            onChange={(e) => setSessionIdOverride(e.target.value)}
            placeholder="Auto-generated if empty"
            disabled={running}
          />
        </div>
        <div className="flex items-center gap-2">
          <Checkbox
            id="wf-profile"
            checked={includeProfile}
            onCheckedChange={(c) => setIncludeProfile(c === true)}
            disabled={running}
          />
          <Label htmlFor="wf-profile" className="font-normal cursor-pointer">
            Include timing profile
          </Label>
        </div>
      </Section>

      {/* ── Response ── */}
      {(hasResponse || running) && (
        <Section title="Response">
          <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm">
            {lastSessionId && (
              <div className="flex items-center gap-2">
                <span className="text-muted-foreground">Session</span>
                <code className="bg-muted px-2 py-0.5 rounded font-mono text-xs break-all">
                  {lastSessionId}
                </code>
                <Button type="button" variant="ghost" size="sm" onClick={copySession} className="h-7 gap-1 px-2">
                  <CopyIcon className="h-3 w-3" />
                </Button>
              </div>
            )}
            {completionMs !== null && (
              <span className="text-muted-foreground text-xs font-mono">
                total {formatMs(completionMs)}
              </span>
            )}
            {running && completionMs === null && (
              <span className="text-muted-foreground text-xs font-mono animate-pulse">
                streaming...
              </span>
            )}
          </div>

          {streamChunks.length > 0 && (
            <div className="space-y-2">
              <Label>Emitted chunks</Label>
              <div className="space-y-1">
                {streamChunks.map((chunk, i) => (
                  <div
                    key={i}
                    className="flex items-center gap-2 bg-muted rounded px-3 py-1.5 text-xs font-mono border border-border animate-in fade-in duration-200"
                  >
                    <span className="text-muted-foreground shrink-0 w-16 text-right tabular-nums">
                      {formatMs(chunk.receivedMs)}
                    </span>
                    <span className="text-muted-foreground shrink-0">{chunk.type}</span>
                    <span className="break-all">
                      {typeof chunk.content === "string"
                        ? chunk.content
                        : JSON.stringify(chunk.content)}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {errorText !== null && (
            <div className="space-y-2">
              <Label className="text-destructive">Error</Label>
              <pre className="bg-destructive/10 rounded-lg p-4 text-xs font-mono overflow-auto whitespace-pre-wrap break-words border border-destructive/20">
                {errorText}
              </pre>
            </div>
          )}

          {assistantPreview !== null && (
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <Label>Output</Label>
                {completionMs !== null && (
                  <span className="text-muted-foreground text-xs font-mono tabular-nums">
                    {formatMs(completionMs)}
                  </span>
                )}
              </div>
              <pre className="bg-muted rounded-lg p-4 text-xs font-mono overflow-auto max-h-[240px] whitespace-pre-wrap break-words border border-border">
                {assistantPreview}
              </pre>
            </div>
          )}

          {completion !== null && (
            <div className="space-y-2">
              <Label className="text-muted-foreground">Full API response</Label>
              <pre className="bg-muted rounded-lg p-4 text-xs font-mono overflow-auto max-h-[320px] whitespace-pre-wrap break-words border border-border">
                {formatJson(completion)}
              </pre>
            </div>
          )}
        </Section>
      )}
    </div>
  );
};

export default WorkflowTestRequestTab;
